export const processRoutingEn = {
  title: 'Process routing',
  subtitle:
    'Versioned work instructions, quality checks, yield and losses for this exact recipe version.',
  statuses: {
    DRAFT: 'Draft',
    IN_REVIEW: 'In review',
    APPROVED: 'Approved',
    BLOCKED: 'Blocked',
    ARCHIVED: 'Archived',
  },
  help: {
    open: 'Open process-routing help',
    header:
      'The stable routing number is assigned to the recipe; every recipe version has its own controlled routing version.',
    translations: 'Names and instructions can be maintained in every supported language.',
    steps:
      'Drag steps into execution order. Decimal factors are calculated on the server without floating-point arithmetic.',
    history:
      'Every routing version remains linked to its recipe version and immutable after submission.',
    saveBeforeDocuments:
      'Save the routing once before uploading process documents and photos to its protected DMS record.',
    snapshotDocuments:
      'Selected protected originals form the immutable document snapshot of this routing version.',
  },
  actions: {
    reorder: 'Reorder process step',
    scaleBatch: 'Scale batch',
    submit: 'Submit for review',
    review: 'Complete review',
    approve: 'Approve',
    copy: 'New routing version',
    block: 'Block',
    archive: 'Archive',
    addStep: 'Add step',
    addByproduct: 'Add by-product',
  },
  messages: {
    saved: 'Process routing saved.',
    invalid: 'Correct the invalid routing data or JSON structures.',
    submitted: 'Process routing submitted for independent review.',
    workflowUpdated: 'Process-routing workflow updated.',
  },
  sections: {
    header: 'Routing header',
    translations: 'Languages',
    steps: 'Process steps',
    history: 'Version history',
    documents: 'Process-routing DMS record',
    snapshotDocuments: 'Version document snapshot',
    stepTranslations: 'Translated step instructions',
    byproducts: 'By-products',
    stepDocuments: 'Step documents and photos',
  },
  fields: {
    number: 'Routing number',
    name: 'Name',
    processFactor: 'Calculated total process factor',
    description: 'Description',
    localizedName: 'Localized name',
    position: 'Position',
    stepName: 'Step name',
    workplace: 'Workplace / area',
    input: 'Input',
    output: 'Output',
    yield: 'Yield %',
    loss: 'Loss %',
    version: 'Routing version',
    recipeVersion: 'Recipe version',
    status: 'Status',
    updated: 'Last change',
    workCenter: 'Work center',
    productionArea: 'Production area',
    inputQuantity: 'Input quantity',
    inputUnit: 'Input unit',
    outputQuantity: 'Output quantity',
    outputUnit: 'Output unit',
    targetTime: 'Target time (min)',
    setupTime: 'Setup time (min)',
    duration: 'Duration (min)',
    temperature: 'Temperature',
    temperatureUnit: 'Temperature unit',
    scrap: 'Scrap %',
    absorption: 'Absorption %',
    instructions: 'Work instructions',
    parameters: 'Configurable parameters (JSON)',
    qualityChecks: 'Quality checks (JSON)',
    product: 'Product',
    document: 'Document',
    photo: 'Photo',
    reason: 'Traceable reason / note',
  },
  states: { noDocuments: 'No protected process documents are available.' },
  stepDialog: {
    title: 'Edit process step',
    description:
      'Maintain quantities, times, parameters, checks, losses, instructions, by-products and controlled evidence.',
  },
  actionDialog: {
    REVIEW: {
      title: 'Complete technical review',
      description: 'Confirm the independent review with a traceable note.',
    },
    APPROVE: {
      title: 'Approve process routing',
      description:
        'Approval freezes the routing and requires an approved recipe and four-eyes separation.',
    },
    COPY: {
      title: 'Create new routing version',
      description: 'Copies this snapshot into a new editable draft with a traceable source link.',
    },
    BLOCK: {
      title: 'Block process routing',
      description: 'The controlled version remains immutable but is no longer usable.',
    },
    ARCHIVE: {
      title: 'Archive process routing',
      description: 'The version remains fully traceable and is removed from active use.',
    },
  },
} as const;

export const batchScalingEn = {
  title: 'Batch scaling',
  subtitle: 'Reproducible server-side material calculation in both planning directions.',
  help: {
    open: 'Open batch-scaling help',
    input: 'Choose an approved recipe, direction, output unit and optional approved substitutions.',
    materials:
      'Requirements include component losses and all nested sub-recipes and are rounded by the target unit.',
    formulas:
      'Every intermediate result and the exact formula used by the server is retained in the response.',
  },
  sections: {
    input: 'Calculation input',
    materials: 'Material requirements',
    limiting: 'Limiting materials',
    subRecipes: 'Resolved sub-recipes',
    byproducts: 'Expected by-products',
    formulas: 'Formula and intermediate results',
    substitutions: 'Approved substitutions',
    available: 'Available starting materials',
  },
  fields: {
    recipe: 'Recipe version',
    direction: 'Calculation direction',
    outputUnit: 'Output unit',
    position: 'Position {{position}}',
    primaryComponent: 'Use primary component',
    targetQuantity: 'Desired final quantity',
    material: 'Material',
  },
  directions: {
    TARGET_OUTPUT: 'Desired final quantity → required materials',
    AVAILABLE_INPUT: 'Available material → possible final quantity',
  },
  actions: { addMaterial: 'Add available material', calculate: 'Calculate batch' },
  result: {
    output: 'Possible / desired output',
    scale: 'Reference scale',
    processFactor: 'Overall process factor',
    referenceOutput: 'Effective reference output',
    level: 'BOM level {{level}}',
  },
  columns: {
    article: 'Article',
    type: 'Type',
    quantity: 'Required quantity',
    source: 'Source paths',
    available: 'Available',
    requiredReference: 'Required for reference',
    possibleOutput: 'Possible output',
  },
  componentTypes: { MATERIAL: 'Material', PACKAGING: 'Packaging' },
  states: { none: 'None.' },
  messages: { invalid: 'The calculation input is invalid.' },
  formulas: {
    PROCESS_STEP_FACTOR: 'Process-step factor',
    OVERALL_PROCESS_FACTOR: 'Overall process factor',
    ROOT_RECIPE_SCALE: 'Root recipe scale',
    COMPONENT_GROSS_REQUIREMENT: 'Gross component requirement',
    SUB_RECIPE_SCALE: 'Sub-recipe scale',
    AVAILABLE_MATERIAL_OUTPUT: 'Possible output from material',
  },
  warnings: {
    PROCESS_ROUTING_MISSING_ASSUMED_100_PERCENT:
      'No process routing exists; 100% process yield is assumed and must be checked.',
    PROCESS_ROUTING_NOT_APPROVED: 'The selected process routing is not approved.',
    STEP_YIELD_MISSING_ASSUMED_100_PERCENT:
      'A process step has no yield or input/output data; 100% is assumed.',
    STEP_YIELD_CONTRADICTS_INPUT_OUTPUT:
      'Maintained yield contradicts the step input/output ratio.',
    RECIPE_VERSION_NOT_APPROVED: 'At least one recipe version in the hierarchy is not approved.',
    COMPONENT_PRODUCT_BLOCKED_OR_ARCHIVED: 'A required product is blocked or discontinued.',
    MATERIAL_AVAILABILITY_NOT_PROVIDED:
      'Availability was not supplied for this required material; it does not limit the result.',
    AVAILABLE_MATERIAL_NOT_REQUIRED:
      'A supplied material is not required by the selected structure.',
  },
} as const;
